import Phaser from 'phaser';
import { HEADER_HEIGHT, TIME_RATE_MULTIPLIER, PRE_OPEN_GAME_SECONDS, OPEN_GAME_SECONDS } from '../config';
import { COLORS, TextStyles, Spacing } from '../ui/theme';
import { store } from '../state/store';
import { events } from '../state/events';
import { baking } from '../systems/BakingSystem';
import { ovenTimers } from '../systems/OvenTimers';
import { NavIcon, type NavIconType } from '../ui/NavIcon';
import { CoinIcon } from '../ui/CoinIcon';

interface NavConfig {
  type: NavIconType;
  scene: 'Menu' | 'Kitchen' | 'Oven' | 'Counter';
}

const NAV: NavConfig[] = [
  { type: 'menu',    scene: 'Menu' },
  { type: 'kitchen', scene: 'Kitchen' },
  { type: 'oven',    scene: 'Oven' },
  { type: 'shop',    scene: 'Counter' },
];

export class HeaderBar extends Phaser.Scene {
  private coinsText!: Phaser.GameObjects.Text;
  private dayText!: Phaser.GameObjects.Text;
  private clockText!: Phaser.GameObjects.Text;
  private highlight!: Phaser.GameObjects.Graphics;
  private icons: { config: NavConfig; container: NavIcon }[] = [];
  private pendingActive: NavConfig['scene'] | null = null;

  constructor() {
    super({ key: 'HeaderBar', active: false });
  }

  create(): void {
    const w = this.scale.width;
    const midY = HEADER_HEIGHT / 2;

    this.add.rectangle(0, 0, w, HEADER_HEIGHT, COLORS.headerPurpleDeep).setOrigin(0, 0);
    this.add.rectangle(0, HEADER_HEIGHT - 4, w, 4, COLORS.headerPurple).setOrigin(0, 0);

    // Logo: orange disc placeholder (a real B logo can replace this later)
    const logoG = this.add.graphics();
    logoG.fillStyle(COLORS.accentOrange, 1).fillCircle(36, midY, 22);
    logoG.fillStyle(COLORS.headerPurpleDeep, 1).fillCircle(36, midY, 9);

    this.add.text(72, midY, 'BAKERY PRO', TextStyles.BRAND).setOrigin(0, 0.5);

    // Nav icons grouped in a pill background centered horizontally.
    const iconSize = 44;
    const iconSpacing = 64;
    const groupW = NAV.length * iconSpacing;
    const groupStart = w / 2 - groupW / 2 + iconSpacing / 2;

    const pillW = groupW + Spacing.md;
    const pillH = HEADER_HEIGHT - 18;
    this.add.graphics()
      .fillStyle(COLORS.headerPurple, 0.7)
      .fillRoundedRect(w / 2 - pillW / 2, midY - pillH / 2, pillW, pillH, pillH / 2);

    const indicatorW = iconSize * 0.78;
    const indicatorH = 5;
    const indicatorY = midY + iconSize * 0.66;
    this.highlight = this.add.graphics();
    this.highlight
      .fillStyle(COLORS.accentOrange, 1)
      .fillRoundedRect(-indicatorW / 2, -indicatorH / 2, indicatorW, indicatorH, indicatorH / 2);
    this.highlight.setPosition(groupStart, indicatorY);
    this.highlight.setVisible(false);

    NAV.forEach((cfg, i) => {
      const x = groupStart + i * iconSpacing;
      const icon = new NavIcon(this, x, midY, {
        type: cfg.type,
        size: iconSize,
        onClick: () => this.requestNav(cfg.scene),
      });
      this.icons.push({ config: cfg, container: icon });
    });

    // Right cluster: coin + counter, then day badge, then phase clock.
    const rightEdge = w - Spacing.lg;

    this.clockText = this.add.text(rightEdge, midY, 'PREP 2:00', TextStyles.MONO_CLOCK).setOrigin(1, 0.5);

    const clockLeft = rightEdge - 210 - Spacing.md;
    this.dayText = this.add.text(clockLeft, midY, 'Day 1', {
      ...TextStyles.LABEL,
      color: '#ffffff',
      fontSize: '24px',
    }).setOrigin(1, 0.5);

    const dayLeft = this.dayText.x - this.dayText.width - Spacing.lg;
    this.coinsText = this.add.text(dayLeft, midY, '000', TextStyles.MONO_COUNTER).setOrigin(1, 0.5);
    const coinSize = 42;
    new CoinIcon(this, this.coinsText.x - this.coinsText.width - coinSize / 2 - Spacing.sm, midY, { size: coinSize });

    this.refresh();
    events.on('coins:changed', () => this.refresh());
    events.on('day:advance', () => this.refresh());
    events.on('state:loaded', () => this.refresh());
    events.on('state:saved', () => this.refresh());

    this.events.on(Phaser.Scenes.Events.WAKE, () => this.refresh());

    // Global game tick in GAME-SECONDS: the day clock, oven bakes, and
    // kitchen stopwatches all advance at TIME_RATE_MULTIPLIER× real time so
    // the player reads one time scale everywhere. HeaderBar is the always-on
    // overlay scene, so it owns the tick. Time holds still on the Welcome
    // screen — the day starts when a baker is picked.
    this.events.on(Phaser.Scenes.Events.UPDATE, (_t: number, dtMs: number) => {
      if (this.scene.isActive('Welcome')) return;
      const dtGame = (dtMs / 1000) * TIME_RATE_MULTIPLIER;
      store.tickClock(dtGame);
      baking.tick(dtGame);
      ovenTimers.tick(dtGame);
      this.refreshClock();
    });

    if (this.pendingActive) {
      this.setActiveScene(this.pendingActive);
      this.pendingActive = null;
    }
  }

  setActiveScene(scene: NavConfig['scene']): void {
    // A BaseGameScene can call this before our create() has built the icons
    // (scene.launch defers creation a frame) — stash and apply when ready.
    if (this.icons.length === 0) {
      this.pendingActive = scene;
      return;
    }
    const idx = NAV.findIndex((c) => c.scene === scene);
    if (idx < 0) {
      this.highlight.setVisible(false);
      return;
    }
    const target = this.icons[idx].container;
    this.highlight.x = target.x;
    this.highlight.setVisible(true);
  }

  private requestNav(scene: NavConfig['scene']): void {
    if (this.scene.isActive('Welcome')) return;
    events.emit('nav:request', { to: scene });
  }

  private refresh(): void {
    const s = store.state;
    const padded = String(s.coins).padStart(3, '0');
    this.coinsText.setText(padded);
    this.dayText.setText(`Day ${s.day}`);
    this.refreshClock();
  }

  private refreshClock(): void {
    const s = store.state;
    if (s.phase === 'pre-open') {
      const remaining = Math.max(0, PRE_OPEN_GAME_SECONDS - s.clockGameSec);
      this.clockText.setText(`PREP ${formatGameClock(remaining)}`);
      this.clockText.setColor('#ffe6b3');
    } else if (s.phase === 'open') {
      const remaining = Math.max(0, PRE_OPEN_GAME_SECONDS + OPEN_GAME_SECONDS - s.clockGameSec);
      this.clockText.setText(`OPEN ${formatGameClock(remaining)}`);
      this.clockText.setColor('#8ef2a0');
    } else {
      this.clockText.setText('CLOSED');
      this.clockText.setColor('#f2a0a0');
    }
  }
}

// Game-seconds → "H:MM" (a 2-game-hour prep shows as 2:00 counting down).
function formatGameClock(gameSec: number): string {
  const totalMin = Math.ceil(gameSec / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}
